import React from 'react';
import { bayWindowProfile } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';
import { GrilleOverlay, normalizePanes } from './WindowGrilleOverlay';
import { DEFAULT_GRILLE_PATTERN } from '../../../constants/windowDefaults';

/**
 * Bay window elevation schematic — the `style === 'bay'` branch of
 * `WindowElevationSchematic`, drawn in METRES from the same
 * `bayWindowProfile()` the 3D `BayWindowGeometry` builds from.
 *
 * Front view (viewBox = the daylight opening, width × height): what an
 * interior elevation sees through the opening — the centre picture unit
 * at full width and the two casement flankers foreshortened to their
 * wall footprint (sash width × cos(bayAngle)), meeting the centre at the
 * mullion posts (centre stile + flanker stile seen through the glass);
 * plus the interior trim outside the opening: the casing head and jambs
 * and the stool with its horns. The head board, the flankers' outer
 * stiles and all the rails sit behind the wall and are not drawn.
 *
 * Side views (viewBox = declared depth × height): the projection profile
 * seen along the wall — casing on the wall face, head board and seat
 * board (with the stool nosing into the room) closing the unit, and the
 * near flanker's frame, glass and sash foreshortened by sin(bayAngle).
 * The wall's interior face is anchored to the viewBox edge that faces the
 * room (right edge in the LEFT view, left edge in the RIGHT view — the
 * same convention as the 3D harness, where the exterior is screen-left
 * looking from -X), and the profile is drawn at true scale from there,
 * overflowing the box wherever the bay projects past the declared depth.
 */
function BayWindowSchematic({
  obj,
  widthMeters,
  heightMeters,
  depthMeters,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const winCat =
    obj && typeof obj.category?.window === 'object' ? obj.category.window : {};
  const profile = bayWindowProfile({
    width: widthMeters,
    bayAngle: Number(winCat.bayAngle) || 45,
    frameThickness: Number(winCat.frameThickness) || undefined,
    trimWidth: winCat.trimWidthSideA ?? winCat.trimWidth ?? undefined,
    trimDepth: winCat.trimDepthSideA ?? winCat.trimDepth ?? undefined,
  });
  const line = {
    fill: 'none',
    stroke: strokeColor,
    strokeWidth,
    vectorEffect: 'non-scaling-stroke',
  };
  if (view === 'left' || view === 'right') {
    return (
      <BaySideView
        profile={profile}
        H={heightMeters}
        depthMeters={depthMeters}
        view={view}
        line={line}
      />
    );
  }
  return (
    <BayFrontView
      profile={profile}
      H={heightMeters}
      panes={normalizePanes(winCat.panes)}
      grillePattern={winCat.grillePattern || DEFAULT_GRILLE_PATTERN}
      line={line}
      grilleStroke={Math.max(strokeWidth * 0.5, 0.3)}
      strokeColor={strokeColor}
    />
  );
}

BayWindowSchematic.supportsSideViews = true;

function BayFrontView({
  profile,
  H,
  panes,
  grillePattern,
  line,
  grilleStroke,
  strokeColor,
}) {
  const {
    width: W,
    wallFootprint: f,
    frameThickness: t,
    cos,
    sashThickness: s,
    casingWidth: cw,
    boardBackHalfWidth,
    boardThickness,
    isBoxBay,
  } = profile;
  // Mullion post between a flanker and the centre: the centre stile plus
  // the flanker's inner stile behind it, foreshortened. A box bay's
  // flankers are edge-on, so its centre spans the whole opening.
  const post = t + t * cos;
  const centreGlass = isBoxBay
    ? { x: 0, w: W }
    : { x: f + t * cos, w: W - 2 * (f + t * cos) };
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {/* Daylight opening */}
      <rect x={0} y={0} width={W} height={H} {...line} />
      {!isBoxBay && (
        <>
          <rect x={f - t} y={0} width={post} height={H} {...line} />
          <rect x={W - f - t * cos} y={0} width={post} height={H} {...line} />
          {/* Casement sash of each flanker: three sides, its inner stile
              is behind the mullion post. */}
          <path
            d={`M ${f - t} ${s} L ${s * cos} ${s} L ${s * cos} ${H - s} L ${f - t} ${H - s}`}
            {...line}
          />
          <path
            d={`M ${W - f + t} ${s} L ${W - s * cos} ${s} L ${W - s * cos} ${H - s} L ${W - f + t} ${H - s}`}
            {...line}
          />
        </>
      )}
      <GrilleOverlay
        sash={{ x: centreGlass.x, y: 0, w: centreGlass.w, h: H }}
        panes={panes}
        grillePattern={grillePattern}
        strokeColor={strokeColor}
        strokeWidth={grilleStroke}
      />
      {/* Interior casing: head over the opening, jambs down to the stool */}
      <path
        d={`M ${-cw} ${H + t} L ${-cw} ${-cw} L ${W + cw} ${-cw} L ${W + cw} ${H + t}`}
        {...line}
      />
      <line x1={0} y1={H} x2={0} y2={H + t} {...line} />
      <line x1={W} y1={H} x2={W} y2={H + t} {...line} />
      {/* Stool: the seat board's nosing, horns past each casing */}
      <rect
        x={W / 2 - boardBackHalfWidth}
        y={H + t}
        width={2 * boardBackHalfWidth}
        height={boardThickness}
        {...line}
      />
    </svg>
  );
}

/**
 * Profile children are drawn in wall coordinates: x = world z (0 at the
 * interior wall face, exterior negative), y = SVG y (0 at the opening
 * top). The wrapper transform places the wall face on the room-side edge
 * of the viewBox for the requested view.
 */
function BaySideView({ profile, H, depthMeters, view, line }) {
  const {
    casingWidth: cw,
    casingDepth: cd,
    frameThickness: t,
    boardThickness: b,
    stoolNosing,
    unitDepth,
    flankNearDepth,
    flankExteriorDepth,
    sin,
    sashThickness: s,
  } = profile;
  const d = depthMeters > 0 ? depthMeters : profile.overallDepth;
  const flankW = flankExteriorDepth - flankNearDepth;
  const children = (
    <g>
      {/* Casing, end-on, proud of the wall face */}
      <rect x={0} y={-cw} width={cd} height={H + cw + t} {...line} />
      {/* Head board on the units' head rails; seat board under their sills,
          running on into the room as the stool */}
      <rect
        x={-unitDepth}
        y={-(t + b)}
        width={unitDepth}
        height={b}
        {...line}
      />
      <rect
        x={-unitDepth}
        y={H + t}
        width={unitDepth + stoolNosing}
        height={b}
        {...line}
      />
      {/* Near flanker: frame, glass line, casement sash */}
      <rect
        x={-flankExteriorDepth}
        y={-t}
        width={flankW}
        height={H + 2 * t}
        {...line}
      />
      <rect
        x={-flankExteriorDepth + t * sin}
        y={0}
        width={flankW - 2 * t * sin}
        height={H}
        {...line}
      />
      <rect
        x={-flankExteriorDepth + (t + s) * sin}
        y={s}
        width={flankW - 2 * (t + s) * sin}
        height={H - 2 * s}
        {...line}
      />
    </g>
  );
  return (
    <svg
      viewBox={`0 0 ${d} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {view === 'right' ? (
        <g transform="scale(-1 1)">{children}</g>
      ) : (
        <g transform={`translate(${d} 0)`}>{children}</g>
      )}
    </svg>
  );
}

export default BayWindowSchematic;
